import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, map, take, takeUntil } from "rxjs/operators";
import { User } from "src/app/core/mad-auth/mad-auth.store";
import { COLUMNS, DISPLAYED_COLUMNS } from "./core/columns";
import { ComparableListItem } from "./core/list-item";
import { ComparableListStore, DEFAULT_SORT } from "./core/comparable-list.store";
import { ComparableFilterDialogComponent, ComparableFilterDialogInput, ComparableFilterDialogOutput } from "./filter-dialog/comparable-filter-dialog.component";

/** One entry of the table's "Add new comparable" menu, when the caller offers a choice. */
export type AddComparableOption = {
    propertySubTypeId: number,
    label: string
}

/**
 * The comparables table. Every row it shows comes from the server already filtered,
 * searched, sorted and paged — see ComparableListStore — so the browser never holds more
 * than the page on screen.
 *
 * Which comparables are selected, and what selecting one does, are the parent's: the
 * comparable transaction and income valuation processes keep that in two different NgRx
 * stores, and this table serves both.
 */
@Component({
    selector: 'app-valuation-comparables',
    templateUrl: './valuation-comparables.component.html',
    styleUrls: ['./valuation-comparables.component.scss']
})
export class ValuationComparablesComponent implements OnInit, OnDestroy {
    @Input() currentUser?: User
    @Input() readonly: boolean = false

    private _selectedRefNums$ = new BehaviorSubject<string[]>([])
    selectedRefNums$ = this._selectedRefNums$.asObservable()
    /** The refs the valuation has selected — shown as picked, and pinned to the top. */
    @Input()
    set selectedRefNums(val: string[]) {
        const refNums = val ?? []
        this._selectedRefNums$.next(refNums)
        // Selecting a comparable changes the order the server returns, so it is part of
        // the query rather than something the table reshuffles afterwards.
        this.listStore.setPinnedRefNums(refNums)
    }

    /** Property types chosen in the map's filter section — the table honours the same set. */
    @Input()
    set propertySubTypeIds(val: number[]) {
        console.log('[cmp-debug] table input propertySubTypeIds', val) // TEMP DEBUG
        this.listStore.setPropertySubTypeIds(val ?? [])
    }

    /**
     * The sub types a comparable may be added as, one menu item each. Null -- the default --
     * leaves the single "Add with form" item, which is what every caller but a Mixed Use
     * target property wants.
     */
    @Input() addComparableOptions: AddComparableOption[] = null

    @Output() onAddComparable: EventEmitter<AddComparableOption> = new EventEmitter()
    @Output() selectComparable: EventEmitter<string> = new EventEmitter()
    @Output() deselectComparable: EventEmitter<string> = new EventEmitter()

    searchControl = new FormControl<string>(null)
    displayedColumns = DISPLAYED_COLUMNS
    /** What the sort headers start on — the store may already hold an earlier choice. */
    initialSort: Sort = DEFAULT_SORT

    dataSource$ = this.listStore.items$
    itemNum$ = this.listStore.total$
    loading$ = this.listStore.loading$
    pageIndex$ = this.listStore.pageIndex$
    readonly pageSize = this.listStore.pageSize

    sort$ = this.listStore.sort$
    sortColumn$ = this.sort$.pipe(
        map(sort => {
            const column = COLUMNS.find(c => c.key == sort.active)
            return column ? column.label : 'N/A'
        })
    )

    private _onDestroy$ = new Subject<void>()

    constructor(
        private dialog: MatDialog,
        private listStore: ComparableListStore
    ) {
        this.listStore.sort$.pipe(take(1)).subscribe(sort => this.initialSort = sort)
    }

    ngOnInit(): void {
        // The store survives leaving the tab, so the box starts on whatever was searched.
        this.listStore.search$.pipe(take(1)).subscribe(search => {
            this.searchControl.setValue(search, { emitEvent: false })
        })
        this.searchControl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(search => {
            this.listStore.setSearch(search)
        })
    }

    ngOnDestroy(): void {
        this._onDestroy$.next()
        this._onDestroy$.complete()
    }

    onSortChange(sort: Sort) {
        this.listStore.setSort(sort)
    }

    onPageChange(event: PageEvent) {
        this.listStore.setPageIndex(event.pageIndex)
    }

    onClickFilter() {
        // The dialog opens first and waits for the value lists itself — they take a server
        // round trip, and resolving them out here left the button looking dead meanwhile.
        // Fixed width: the dialog shows one column's values at a time, so its content no
        // longer dictates a sensible size.
        const dialogRef = this.dialog.open<ComparableFilterDialogComponent, ComparableFilterDialogInput, ComparableFilterDialogOutput>(
            ComparableFilterDialogComponent,
            { data: { columns: this.listStore.filtersForDialog() }, width: '520px' }
        )
        dialogRef.afterClosed().pipe(takeUntil(this._onDestroy$)).subscribe(res => {
            if (!res) return;
            this.listStore.setFilters(res.filters)
        })
    }

    onClickReset() {
        this.searchControl.setValue(null, { emitEvent: false })
        this.listStore.reset()
    }

    onClickAddComparable(option: AddComparableOption) {
        this.onAddComparable.emit(option)
    }

    /** Naming the sub type is only worth it when there is more than one to choose between. */
    addComparableLabel(option: AddComparableOption): string {
        return this.addComparableOptions && this.addComparableOptions.length > 1
            ? `Add ${option.label} with form`
            : 'Add with form'
    }

    onSelect(item: ComparableListItem, isSelected: boolean) {
        if (isSelected) {
            this.deselectComparable.emit(item.refNum)
            return
        }
        // The list rows are slim; selecting must load the full comparable, which is what
        // the parent's action does before selecting it into the valuation process.
        this.selectComparable.emit(item.refNum)
    }

    isSelected(refNums: string[], item: ComparableListItem): boolean {
        return refNums.includes(item.refNum)
    }
}
